import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { ehsService } from '../../services/ehsService';

const initialState = {
  incidents: [],
  incident: null,
  incidentTypes: [],
  severityLevels: [],
  incidentStatuses: [],
  isLoading: false,
  error: null,
};

const makeListThunk = (name, serviceCall) =>
  createAsyncThunk(`ehs/${name}`, async (params = {}, { rejectWithValue }) => {
    try {
      const response = await serviceCall(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || `Error fetching ${name}`);
    }
  });

export const fetchIncidents = makeListThunk('fetchIncidents', ehsService.getIncidents);
export const fetchIncidentTypes = makeListThunk('fetchIncidentTypes', ehsService.getIncidentTypes);
export const fetchSeverityLevels = makeListThunk('fetchSeverityLevels', ehsService.getSeverityLevels);
export const fetchIncidentStatuses = makeListThunk('fetchIncidentStatuses', ehsService.getIncidentStatuses);

export const fetchIncident = createAsyncThunk(
  'ehs/fetchIncident',
  async (id, { rejectWithValue }) => {
    try {
      const response = await ehsService.getIncident(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Error fetching incident');
    }
  }
);

export const createIncident = createAsyncThunk(
  'ehs/createIncident',
  async (data, { rejectWithValue }) => {
    try {
      const response = await ehsService.createIncident(data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Error creating incident');
    }
  }
);

export const updateIncident = createAsyncThunk(
  'ehs/updateIncident',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await ehsService.updateIncident(id, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Error updating incident');
    }
  }
);

export const deleteIncident = createAsyncThunk(
  'ehs/deleteIncident',
  async (id, { rejectWithValue }) => {
    try {
      await ehsService.deleteIncident(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Error deleting incident');
    }
  }
);

const normalizeList = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.results)) return payload.results;
  return [];
};

const addListCases = (builder, thunk, stateKey) => {
  builder
    .addCase(thunk.pending, (state) => { state.isLoading = true; state.error = null; })
    .addCase(thunk.fulfilled, (state, action) => {
      state.isLoading = false;
      state[stateKey] = normalizeList(action.payload);
    })
    .addCase(thunk.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    });
};

const ehsSlice = createSlice({
  name: 'ehs',
  initialState,
  reducers: {
    clearEhsError: (state) => { state.error = null; },
    clearIncident: (state) => { state.incident = null; },
  },
  extraReducers: (builder) => {
    addListCases(builder, fetchIncidents, 'incidents');
    addListCases(builder, fetchIncidentTypes, 'incidentTypes');
    addListCases(builder, fetchSeverityLevels, 'severityLevels');
    addListCases(builder, fetchIncidentStatuses, 'incidentStatuses');

    builder
      .addCase(fetchIncident.pending, (state) => { state.isLoading = true; state.error = null; })
      .addCase(fetchIncident.fulfilled, (state, action) => {
        state.isLoading = false;
        state.incident = action.payload;
      })
      .addCase(fetchIncident.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    builder
      .addCase(createIncident.fulfilled, (state, action) => {
        state.incident = action.payload;
        if (Array.isArray(state.incidents)) {
          state.incidents = [action.payload, ...state.incidents];
        }
      })
      .addCase(updateIncident.fulfilled, (state, action) => {
        state.incident = action.payload;
        if (Array.isArray(state.incidents)) {
          state.incidents = state.incidents.map((i) =>
            i.id === action.payload.id ? action.payload : i
          );
        }
      })
      .addCase(deleteIncident.fulfilled, (state, action) => {
        state.incidents = (state.incidents || []).filter((i) => i.id !== action.payload);
      });
  },
});

export const { clearEhsError, clearIncident } = ehsSlice.actions;
export default ehsSlice.reducer;
